// Page and article sticky link bar. Toggles visibility, focus, and the chat offset. -- SG 20261012
export class ContentStickyCta extends HTMLElement {
	private readonly focusableSelector = 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
	private readonly viewportQuery = window.matchMedia('(min-width: 1024px)');
	private heroObserver: IntersectionObserver | null = null;
	private footerObserver: IntersectionObserver | null = null;
	private resizeObserver: ResizeObserver | null = null;
	private chatObserver: MutationObserver | null = null;
	private heroSeen = new Set<Element>();
	private heroBlocks = false;
	private footerBlocks = false;
	private outsideFocus: HTMLElement | null = null;
	private chatBottom: string | null = null;
	private chatRestoring = false;

	// Wire observers once the element is in the document. -- SG 20261012
	connectedCallback() {
		this.addEventListener('focusin', this.onFocusIn);
		this.viewportQuery.addEventListener('change', this.onViewportChange);
		this.resizeObserver = new ResizeObserver(() => {
			this.observeFooter();
			if (this.classList.contains('active')) {
				this.publishOffset();
			}
		});
		this.resizeObserver.observe(this);
		this.watchChat();
		this.observeHeroes();
		this.footerBlocks = this.readFooterBlocks();
		this.observeFooter();
		this.sync();
	}

	// Drop observers and undo offsets if the section is removed while open. -- SG 20261012
	disconnectedCallback() {
		this.heroObserver?.disconnect();
		this.footerObserver?.disconnect();
		this.resizeObserver?.disconnect();
		this.chatObserver?.disconnect();
		this.viewportQuery.removeEventListener('change', this.onViewportChange);
		this.removeEventListener('focusin', this.onFocusIn);
		this.heroObserver = null;
		this.footerObserver = null;
		this.resizeObserver = null;
		this.chatObserver = null;
		if (this.classList.contains('active')) {
			this.classList.remove('active');
			document.documentElement.style.removeProperty('--content-sticky-cta-offset');
			this.restoreChat();
		}
	}

	// Remember the control that focused into the bar, when it sits outside. -- SG 20261012
	private onFocusIn = (event: FocusEvent) => {
		const related = event.relatedTarget;
		if (related instanceof HTMLElement && !this.contains(related)) {
			this.outsideFocus = related;
		}
	};

	// Re-check mobile-only visibility after the breakpoint changes. -- SG 20261012
	private onViewportChange = () => {
		this.observeFooter();
		this.sync();
	};

	// Hide while an index hero is on screen. Show immediately when none exists. -- SG 20261012
	private observeHeroes() {
		const heroes = document.querySelectorAll('.section__index-hero-carousel, .section__index-hero-banner');
		if (heroes.length === 0) {
			this.heroBlocks = false;
			return;
		}
		this.heroBlocks = true;
		this.heroObserver = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					this.heroSeen.add(entry.target);
					return;
				}
				this.heroSeen.delete(entry.target);
			});
			this.heroBlocks = this.heroSeen.size > 0;
			this.sync();
		});
		heroes.forEach((hero) => this.heroObserver?.observe(hero));
	}

	// Hide once the footer enters the bar's own height at the bottom of the viewport. -- SG 20261012
	private observeFooter() {
		this.footerObserver?.disconnect();
		const footer = document.querySelector('.footer');
		if (!footer) {
			this.footerBlocks = false;
			this.footerObserver = null;
			return;
		}
		const inset = Math.max(0, Math.round(this.offsetHeight));
		this.footerObserver = new IntersectionObserver((entries) => {
			this.footerBlocks = entries.some((entry) => entry.isIntersecting);
			this.sync();
		}, {
			root: null,
			rootMargin: `0px 0px -${inset}px 0px`,
			threshold: 0,
		});
		this.footerObserver.observe(footer);
	}

	// True when the footer top is already inside the bar zone. -- SG 20261012
	private readFooterBlocks() {
		const footer = document.querySelector('.footer');
		if (!footer) return false;
		return footer.getBoundingClientRect().top <= window.innerHeight - this.offsetHeight;
	}

	// Mobile only is display:none from 1024px, so it must not take the offset. -- SG 20261012
	private suppressed() {
		return this.classList.contains('content-sticky-cta--mobile-only') && this.viewportQuery.matches;
	}

	// Apply hero, footer, and viewport together. -- SG 20261012
	private sync() {
		const show = !this.heroBlocks && !this.footerBlocks && !this.suppressed();
		this.setActive(show);
	}

	// Toggle the open state. Move focus out before hiding the bar. -- SG 20261012
	private setActive(isActive: boolean) {
		const wasActive = this.classList.contains('active');
		if (isActive) {
			if (!wasActive) {
				this.classList.add('active');
				this.removeAttribute('aria-hidden');
				this.removeAttribute('inert');
				this.setFocusLocked(false);
			}
			this.publishOffset();
			return;
		}

		if (wasActive && this.contains(document.activeElement)) {
			this.releaseFocus();
		}
		this.classList.remove('active');
		this.setAttribute('aria-hidden', 'true');
		this.setAttribute('inert', '');
		this.setFocusLocked(true);
		if (wasActive) {
			document.documentElement.style.removeProperty('--content-sticky-cta-offset');
			this.restoreChat();
		}
	}

	// Lock or unlock controls inside this bar only. -- SG 20261012
	private setFocusLocked(locked: boolean) {
		this.querySelectorAll<HTMLElement>(this.focusableSelector).forEach((element) => {
			if (locked) {
				element.setAttribute('tabindex', '-1');
				return;
			}
			if (element.getAttribute('tabindex') === '-1') {
				element.removeAttribute('tabindex');
			}
		});
	}

	// Send focus to the previous outside control, or to main. -- SG 20261012
	private releaseFocus() {
		const previous = this.outsideFocus;
		if (previous && previous.isConnected && !this.contains(previous)) {
			previous.focus({ preventScroll: true });
			return;
		}
		const main = document.querySelector('main');
		if (!(main instanceof HTMLElement)) return;
		if (!main.hasAttribute('tabindex')) {
			main.setAttribute('tabindex', '-1');
		}
		main.focus({ preventScroll: true });
	}

	// Publish the bar height for scroll padding and lift the chat button. -- SG 20261012
	private publishOffset() {
		const height = this.offsetHeight;
		document.documentElement.style.setProperty('--content-sticky-cta-offset', `${height}px`);
		this.liftChat(height);
	}

	// Gorgias injects iframe#chat-button after load. -- SG 20261012
	private watchChat() {
		this.chatObserver = new MutationObserver(() => {
			if (!this.classList.contains('active')) return;
			this.liftChat(this.offsetHeight);
		});
		this.chatObserver.observe(document.documentElement, { childList: true, subtree: true });
	}

	// Save the chat iframe's inline bottom once, then sit it above the bar. -- SG 20261012
	private liftChat(height: number) {
		if (this.chatRestoring || !this.classList.contains('active')) return;
		const chat = document.querySelector('iframe#chat-button');
		if (!(chat instanceof HTMLIFrameElement)) return;
		if (this.chatBottom === null) {
			this.chatBottom = chat.style.bottom;
		}
		chat.style.bottom = `${height}px`;
	}

	// Put the chat iframe bottom back, including an empty inline value. -- SG 20261012
	private restoreChat() {
		if (this.chatBottom === null) return;
		this.chatRestoring = true;
		const chat = document.querySelector('iframe#chat-button');
		if (chat instanceof HTMLIFrameElement) {
			chat.style.bottom = this.chatBottom;
		}
		this.chatBottom = null;
		this.chatRestoring = false;
	}
}

customElements.define('content-sticky-cta', ContentStickyCta);
